import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import CornerCard from './CornerCard';
import ChassisBottomPanel from './ChassisBottomPanel';
import { mapToCorners, buildFieldConfig } from '../../utils/setupDataMapper';
import { getTire } from '../../utils/tiresService';

/**
 * Vista car-centric: griglia 2×2 degli angoli + pannello chassis in basso.
 *
 * Retrocompatibile: legge la struttura Firestore originale via mapToCorners().
 * Non scrive mai su Firestore.
 *
 * Props:
 *   setupData              – il campo setupData del documento Firestore
 *   setupTemplate          – il documento template (con .modules[])
 *   totalCalculatedWeight  – calcolato nel genitore (SetupDetail)
 *   calculatedBallastValue – stringa calcolata nel genitore
 *   t                      – funzione di traduzione (useTranslation('setup'))
 */
const CarCentricView = ({ setupData, setupTemplate, totalCalculatedWeight, calculatedBallastValue, t }) => {

  const corners    = useMemo(() => mapToCorners(setupData), [setupData]);
  const fieldConfig = useMemo(() => buildFieldConfig(setupTemplate?.modules), [setupTemplate]);

  // Fetch live data delle gomme collegate (per usura aggiornata)
  const [tireLiveData, setTireLiveData] = useState({});  // { tireId: tireDoc }
  useEffect(() => {
    const ids = ['FL', 'FR', 'RL', 'RR']
      .map(tag => corners[tag]?.tireId)
      .filter(Boolean);
    if (ids.length === 0) return;
    let cancelled = false;
    Promise.all(ids.map(id => getTire(id).catch(() => null)))
      .then(tires => {
        if (cancelled) return;
        const map = {};
        tires.forEach(t => { if (t) map[t.id] = t; });
        setTireLiveData(map);
      });
    return () => { cancelled = true; };
  }, [corners.FL?.tireId, corners.FR?.tireId, corners.RL?.tireId, corners.RR?.tireId]); // eslint-disable-line

  const cornerProps = (tag) => {
    const data = corners[tag] || {};
    const liveTire = data.tireId ? tireLiveData[data.tireId] : null;
    return {
      tag,
      data: liveTire ? { ...data, _liveTire: liveTire } : data,
      setupData,
      fieldConfig,
      t,
    };
  };

  return (
    <Box sx={{ mt: 1.5 }}>

      {/* Etichette asse — solo su schermi ≥ sm */}
      <Box sx={{
        display: { xs: 'none', sm: 'flex' },
        justifyContent: 'space-between',
        px: 0.5, mb: 0.5,
      }}>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>
          ← Sinistra
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>
          Anteriore ↑
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 9 }}>
          Destra →
        </Typography>
      </Box>

      {/* Griglia 4 angoli: 2×2 su sm+, 1 col su xs */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.5,
        alignItems: 'start',
      }}>
        <CornerCard {...cornerProps('FL')} />
        <CornerCard {...cornerProps('FR')} />
        <CornerCard {...cornerProps('RL')} />
        <CornerCard {...cornerProps('RR')} />
      </Box>

      {/* Pannello chassis + tabs */}
      <ChassisBottomPanel
        setupData={setupData}
        fieldConfig={fieldConfig}
        t={t}
        totalCalculatedWeight={totalCalculatedWeight}
        calculatedBallastValue={calculatedBallastValue}
      />

    </Box>
  );
};

export default CarCentricView;
